import { useEffect, useState, FC, useMemo, useCallback } from 'react';
import { Box, Flex, Tag, TagLabel, TagRightIcon } from '@chakra-ui/react';
import { ChakraAutocomplete } from 'app/components/Core';
import { ReactComponent as SearchSvg } from 'resources/images/search.svg';
import { ReactComponent as CloseIcon } from 'resources/images/close.svg';
import { GenrePayload, PatchOperation } from 'types/releases';
import { Genre } from 'types/song';
import { useAppDispatch } from 'app/store';
import { updateSongEffect } from 'app/store/SongList/effects';
import { getGenreValues } from '../utils';

interface GenreOptions extends Genre {
  type: string;
  selected?: boolean;
}

interface GenreOptionsIndex {
  [value: string]: GenreOptions;
}

export interface SongGenreSectionProps {
  selected?: Genre[];
  genreOptions: Genre[];
  value?: GenrePayload[] | null;
}

export const SongGenre: FC<SongGenreSectionProps> = ({
  genreOptions: allGenreOptions,
  selected = [],
}) => {
  const dispatch = useAppDispatch();
  const [selectedGenres, setSelectedGenres] = useState(selected);

  useEffect(() => {
    setSelectedGenres(selected);
  }, [selected]);

  const [inputValue, setInputValue] = useState('');
  const [selectedGenre, setSelectedGenre] = useState<GenreOptionsIndex>({});
  const [genreOptions, setGenreOptions] = useState<GenreOptionsIndex>({});
  const optionsList = useMemo(
    () =>
      Object.values(genreOptions)
        .filter((a) => !a.selected)
        .sort((a: GenreOptions, b: GenreOptions) =>
          b.type.localeCompare(a.type)
        ),
    [genreOptions]
  );

  const handleAction = async (op: PatchOperation, genre: string) => {
    dispatch(
      updateSongEffect({
        formData: { genres: [{ op, genre }] },
      })
    );
  };

  const onSelectGenre = (genre: GenreOptions) => {
    const selected: GenreOptionsIndex = {};
    selected[genre.value] = genre;
    setSelectedGenre({ ...selectedGenre, ...selected });
    const allGenreOptions: GenreOptionsIndex = { ...genreOptions };
    allGenreOptions[genre.value] = { ...genre, selected: true };
    setGenreOptions(allGenreOptions);
    handleAction('ADD', genre.value);
  };

  const handleDelete = (genre: GenreOptions) => {
    const allSelected: GenreOptionsIndex = { ...selectedGenre };
    delete allSelected[genre.value];
    setSelectedGenre(allSelected);
    const allGenreOptions: GenreOptionsIndex = { ...genreOptions };
    allGenreOptions[genre.value] = { ...genre, selected: false };
    setGenreOptions(allGenreOptions);
    handleAction('REMOVE', genre.value);
  };

  const initializeDialogItems = useCallback(() => {
    const selectedGenreById = selectedGenres.reduce(
      (acc: GenreOptionsIndex, curr: Genre) => {
        acc[curr.value] = { ...curr, type: 'default', selected: true };
        return acc;
      },
      {}
    );
    setSelectedGenre(selectedGenreById);

    const genreListById = allGenreOptions.reduce(
      (acc: GenreOptionsIndex, curr: Genre) => {
        acc[curr.value] = {
          ...curr,
          type: 'default',
          selected: getGenreValues(selectedGenres).includes(curr.value),
        };

        return acc;
      },
      {}
    );

    setGenreOptions(genreListById);
  }, [allGenreOptions, selectedGenres]);

  useEffect(() => {
    initializeDialogItems();
  }, [initializeDialogItems]);

  return (
    <Box w="full">
      <ChakraAutocomplete<GenreOptions>
        id="song-genre"
        openMenuOnFocus
        inputValue={inputValue}
        setInputValue={setInputValue}
        options={optionsList}
        value={null}
        noOptionsMessage={() => 'No match found'}
        customComponentsProps={{
          inputLeftElement: <SearchSvg height="16px" width="17px" />,
        }}
        placeholder="Search Genres"
        onChange={(option) => {
          if (option) {
            onSelectGenre(option);
          }
          setInputValue('');
        }}
        onInputChange={(_, meta) => {
          if (meta.action === 'input-blur') {
            setInputValue('');
          }
        }}
        getOptionLabel={(option) => option?.title || ''}
      />
      {Object.values(selectedGenre).length > 0 && (
        <Flex data-testid="genre-chips" mb="22px" flexWrap="wrap" gap={4}>
          {Object.values(selectedGenre).map((data: GenreOptions) => (
            <Tag key={data.value} data-testid={`${data.value}-chip`}>
              <TagLabel>{data.title}</TagLabel>
              <TagRightIcon
                color="iconBase"
                onClick={() => handleDelete(data)}
                _hover={{ cursor: 'pointer', color: 'M.70' }}
                boxSize="12px"
                ml={1}
                as={CloseIcon}
                data-testid={`delete-${data.value}`}
              />
            </Tag>
          ))}
        </Flex>
      )}
    </Box>
  );
};
